import React, { useRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Menu, Button } from '../../../dbWebUI'
import { Icon, Text, Box, Input } from '@deseretbook/react-ui'
import useDropdown from '../../../hooks/useDropdown'

const Bookmark = ({
  bookmark,
  start,
  documentId: bookmarkDocId,
  tableOfContents,
  onBookmarkClick,
  onUpdateBookmark,
  onDeleteBookmark,
}) => {
  const chapter = tableOfContents.byDocumentId[bookmarkDocId]
  const [isEditing, setIsEditing] = useState(false)
  const [name, setName] = useState(bookmark.name)
  const { dropdownProps, triggerProps } = useDropdown()
  const bookmarkRef = useRef(null)

  const onChange = (e) => {
    setName(e.target.value)
  }

  const onSubmit = () => {
    onUpdateBookmark({ ...bookmark, name }, false)
    setIsEditing(false)
  }

  // Handle closing the dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        bookmarkRef.current &&
        !bookmarkRef.current.contains(event.target) &&
        dropdownProps.triggerRef.current &&
        !dropdownProps.triggerRef.current.contains(event.target)
      ) {
        dropdownProps.close()
      }
    }

    if (dropdownProps.isOpen) {
      document.addEventListener('mouseup', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mouseup', handleClickOutside)
    }
  }, [dropdownProps, dropdownProps.isOpen, dropdownProps.close, dropdownProps.triggerRef])

  return (
    <>
      <Menu.ListItem style={{ position: 'relative' }} className="bookmark-list-item">
        <Box alignItems="center" justifyContent="space-between">
          <Box alignItems="center">
            <Icon name="bookmarkSolid" style={{ marginRight: '20px', minWidth: '24px' }} />
            <Box>
              <Box
                alignItems="start"
                justifyContent="start"
                isColumn
                style={{ marginBottom: '8px' }}
              >
                {isEditing ? (
                  <Box alignItems="start">
                    <Input
                      id="name"
                      name="name"
                      labelContent="Name"
                      className="bookmark-list-input"
                      onChange={onChange}
                      defaultValue={bookmark.name}
                      value={name}
                      autoComplete="off"
                      autoFocus
                      removeHint
                    />
                    <Button onClick={onSubmit} className="bookmark-list-submit-button">
                      Save
                    </Button>
                  </Box>
                ) : (
                  <Text
                    style={{
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 700,
                      color: '#000000A6',
                      fontSize: '16px',
                    }}
                  >
                    {bookmark.name}
                  </Text>
                )}
                <Text
                  style={{
                    fontFamily: 'Roboto, sans-serif',
                    fontWeight: 500,
                    color: '#000000A6',
                    fontSize: '14px',
                  }}
                >
                  Page 32
                </Text>
              </Box>
              <Text
                style={{
                  fontFamily: 'Roboto, sans-serif',
                  fontWeight: 400,
                  color: '#000000A6',
                  fontSize: '16px',
                }}
              >
                {chapter && chapter.label ? chapter.label : ''}
              </Text>
            </Box>
          </Box>
          <Box {...triggerProps} as="button" className="bookmark-dropdown-trigger">
            <Icon name="options" />
          </Box>
        </Box>
        <Box
          {...dropdownProps}
          ref={bookmarkRef}
          className="bookmark-dropdown"
          style={{ display: dropdownProps.isOpen ? 'block' : 'none' }}
        >
          <Box isColumn alignItems="start">
            <Button
              className="bookmark-dropdown-button"
              onClick={() => onBookmarkClick(start, bookmarkDocId)}
              style={{ border: 'none' }}
            >
              <Icon className="bookmark-dropdown-icon" name="arrowRight" />
              <Text className="bookmark-dropdown-text">Go to Bookmark</Text>
            </Button>
            <Button
              className="bookmark-dropdown-button"
              onClick={() => {
                setIsEditing(true)
                dropdownProps.close()
              }}
              style={{ border: 'none' }}
            >
              <Icon className="bookmark-dropdown-icon" name="editOutline" />
              <Text className="bookmark-dropdown-text">Rename Bookmark</Text>
            </Button>
            <Button
              className="bookmark-dropdown-button"
              onClick={onDeleteBookmark}
              style={{ border: 'none' }}
            >
              <Icon className="bookmark-dropdown-icon" name="trashOutline" />
              <Text className="bookmark-dropdown-text">Delete Bookmark</Text>
            </Button>
          </Box>
        </Box>
      </Menu.ListItem>
    </>
  )
}

/**
 * Bookmark list component.
 *
 * @returns {function} Component
 */
function BookmarkList({
  bookmarks,
  tableOfContents,
  onBookmarkClick,
  onEmptyListClick,
  onUpdateBookmark,
  ...rest
}) {
  return (
    <Menu hasVerticalScroll {...rest} style={{ height: '80vh' }}>
      <Menu.List>
        {bookmarks.map((bookmarkProps) => {
          return (
            <Bookmark
              key={bookmarkProps.bookmark.id}
              {...bookmarkProps}
              tableOfContents={tableOfContents}
              onBookmarkClick={onBookmarkClick}
              onUpdateBookmark={onUpdateBookmark}
              onDeleteBookmark={() => onUpdateBookmark(bookmarkProps, true)}
            />
          )
        })}
      </Menu.List>
    </Menu>
  )
}

BookmarkList.defaultProps = {
  onBookmarkClick: () => {},
  onEmptyListClick: () => {},
}

BookmarkList.propTypes = {
  bookmarks: PropTypes.arrayOf(PropTypes.object).isRequired,
  tableOfContents: PropTypes.shape({}).isRequired,
  onBookmarkClick: PropTypes.func,
  onEmptyListClick: PropTypes.func,
}

export default BookmarkList
